// Home page: hero field with its load choreography, marquee, card glyphs, the peek field and the
// pinned statement. Each init checks for its own DOM and is a no-op on other pages.
import {
  clamp01,
  DotField,
  easeOut,
  heroSample,
  mountGlyph,
  noise3,
  onFrame,
  smooth,
  state,
  viewportWatcher,
} from "./core.ts"

/** Hero dot field: sparse to dense on load, holes kept clear behind the lede and right eyebrow. */
export function initHero(): void {
  const hero = document.querySelector<HTMLElement>(".hero")
  const cv = hero?.querySelector("canvas")
  if (!hero || !cv) return
  const f = new DotField(cv, {
    sample: heroSample,
    pointer: true,
    pointerHost: hero,
    lo: 1,
    hi: 5,
    intro: state.reduced ? 1 : 0,
    onResize() {
      const hr = cv.getBoundingClientRect()
      this.holes = [...hero.querySelectorAll(".hero-side .lede, .eyebrow-r")].map((el) => {
        const r = el.getBoundingClientRect()
        return {
          l: r.left - hr.left - 12,
          r: r.right - hr.left + 12,
          t: r.top - hr.top - 10,
          b: r.bottom - hr.top + 10,
        }
      })
    },
  })
  let t = 0
  onFrame((dt) => {
    if (f.intro < 1) {
      t += dt
      f.intro = easeOut((t - 0.1) / 1.6)
    }
  })
}

/** The "in the yard" marquee: duplicated list, speed kept at the measured 41 px/s at any width. */
export function initMarquee(): void {
  const track = document.getElementById("track")
  const list = track?.querySelector("ul")
  if (!track || !list) return
  const clone = list.cloneNode(true)
  if (clone instanceof Element) {
    clone.setAttribute("aria-hidden", "true")
    track.appendChild(clone)
  }
  new ResizeObserver(() =>
    track.style.setProperty("--marq-dur", `${list.getBoundingClientRect().width / 41}s`),
  ).observe(list)
}

export function initGlyphs(): void {
  for (const c of document.querySelectorAll<HTMLCanvasElement>("canvas[data-glyph]")) mountGlyph(c)
}

export function initPeek(): void {
  const c = document.getElementById("peek")
  if (!(c instanceof HTMLCanvasElement)) return
  new DotField(c, {
    sample(x, y, t) {
      const v = y / this.h
      const e = Math.exp(-(((x / this.w - (0.9 - v * 0.7)) / 0.35) ** 2))
      return (
        e *
        smooth(0, 0.2, v) *
        smooth(0, 0.25, 1 - v) *
        (0.55 + 0.5 * (noise3(x / 60, y / 60, t * 0.85) * 0.5 + 0.5))
      )
    },
  })
}

/** Splits every text node under `node` into word spans, keeping the whitespace between them. */
function splitWords(node: Node, words: HTMLElement[]): void {
  for (const n of [...node.childNodes]) {
    if (n instanceof Text) {
      const frag = document.createDocumentFragment()
      for (const part of (n.textContent ?? "").split(/(\s+)/)) {
        if (!part) continue
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part))
          continue
        }
        const s = document.createElement("span")
        s.className = "w"
        s.textContent = part
        frag.appendChild(s)
        words.push(s)
      }
      n.replaceWith(frag)
    } else if (n instanceof Element) splitWords(n, words)
  }
}

/** (1) The pinned statement: words light by scroll progress; the field behind it intensifies. */
export function initPin(): void {
  const pin = document.querySelector<HTMLElement>(".pin")
  const stmt = pin?.querySelector(".statement")
  const canvas = pin?.querySelector("canvas")
  if (!pin || !stmt || !canvas) return
  const meter = pin.querySelector<HTMLElement>(".pin-meter")
  const words: HTMLElement[] = []
  splitWords(stmt, words)
  const field = new DotField(canvas, {
    pitch: 9,
    min: 2,
    max: 6,
    lo: 0,
    hi: 3,
    floor: 0.09,
    gain: state.reduced ? 0.6 : 0.15,
    sample(x, y, t) {
      const u = x / this.w
      const v = y / this.h
      const edge = smooth(0.25, 0.95, Math.max(Math.abs(u - 0.5) * 2, Math.abs(v - 0.5) * 1.6))
      const diag = Math.exp(-(((u - (1.05 - v * 0.55)) / 0.12) ** 2))
      return Math.max(edge * 0.55, diag) * (0.35 + 0.75 * (noise3(x / 160, y / 160, t * 0.5) * 0.5 + 0.5))
    },
  })
  let lastP = -1
  const moved = viewportWatcher()
  onFrame(() => {
    if (state.reduced) {
      if (lastP !== 2) {
        for (const w of words) w.style.setProperty("--k", "1")
        lastP = 2
      }
      return
    }
    if (!moved() && lastP >= 0) return
    const r = pin.getBoundingClientRect()
    const p = clamp01(-r.top / Math.max(1, r.height - innerHeight))
    if (Math.abs(p - lastP) < 0.0005) return
    lastP = p
    const lit = p * (words.length + 3) - 1
    words.forEach((w, i) => {
      w.style.setProperty("--k", smooth(0, 1, lit - i).toFixed(3))
    })
    meter?.style.setProperty("--p", p.toFixed(4))
    field.gain = 0.12 + 0.48 * easeOut(p)
  })
}
